import React from 'react';
import { NavLink } from 'react-router-dom';
import { Home, Users, ClipboardList, PersonStanding, Dumbbell, UserPlus, Filter } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useCanAccess } from '@/components/authnz/AbilityContext';

export interface BottomNavItem {
  label: string;
  to: string;
  icon: React.ReactNode;
}

// Define the primary routes for the app. Ensure labels are concise.
// On mobile: Show Library instead of Planner (planner accessible via bridge from library).
// Settings is available via the sidebar drawer, not the bottom bar.
const defaultNavItems: BottomNavItem[] = [
  { label: 'Home', to: '/dashboard', icon: <Home className="h-5 w-5" /> },
  { label: 'Clients', to: '/clients', icon: <Users className="h-5 w-5" /> },
  { label: 'Posture', to: '/posture-analysis', icon: <PersonStanding className="h-5 w-5" /> },
  { label: 'Assessments', to: '/sop-assessment', icon: <ClipboardList className="h-5 w-5" /> },
  { label: 'Library', to: '/drills', icon: <Dumbbell className="h-5 w-5" /> },
];

export interface BottomNavProps {
  items?: BottomNavItem[];
  className?: string;
}

/**
 * BottomNav displays a persistent bottom navigation bar on mobile devices.
 * It uses NavLink from react-router-dom to highlight the active route.
 * Hidden on desktop (md and above), visible only on mobile.
 */
const salesNavItems: BottomNavItem[] = [
  { label: 'Prospects', to: '/sales/prospects', icon: <UserPlus className="h-5 w-5" /> },
  { label: 'Funnel', to: '/sales/funnel', icon: <Filter className="h-5 w-5" /> },
];

export const BottomNav: React.FC<BottomNavProps> = ({ items, className }) => {
  const canReadProspects = useCanAccess('read', 'ClientProspect');
  const canAccessDashboard = useCanAccess('access', 'Dashboard');
  const resolvedItems = items ?? (canReadProspects && !canAccessDashboard ? salesNavItems : defaultNavItems);

  return (
    <nav
      aria-label="Mobile navigation"
      className={cn(
        'shrink-0 w-full border-t border-border bg-card pb-[env(safe-area-inset-bottom,0px)] lg:hidden',
        className
      )}
    >
      <ul className="flex justify-around items-center h-14">
        {resolvedItems.map((item) => (
          <li key={item.to} className="flex-1">
            <NavLink
              to={item.to}
              className={({ isActive }) =>
                cn(
                  'flex flex-col items-center justify-center text-xs h-full transition-colors',
                  isActive
                    ? 'text-primary font-medium'
                    : 'text-muted-foreground'
                )
              }
            >
              {item.icon}
              <span className="mt-1">{item.label}</span>
            </NavLink>
          </li>
        ))}
      </ul>
    </nav>
  );
};

export default BottomNav;

